// src/pages/accounting/Accounting.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Tab,
  Tabs,
  Alert
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  Event as EventIcon,
  School as SchoolIcon,
  ShoppingCart as ShoppingCartIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Euro as EuroIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';

// Chart components
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, LineElement, PointElement } from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';

// Services
import AccountingService from '../../../services/AccountingService';

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

// Paleta de colores verde pastel
const Colors = {
  primary: '#7FB069',
  primaryLight: '#A8D5BA',
  primaryDark: '#6B8E5A',
  secondary: '#000000',
  accent: '#90C695',
  background: '#F0F8F0',
  surface: '#FFFFFF',
  text: '#000000',
  textSecondary: '#724CA7',
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#f44336',
  info: '#2196F3'
};

const Accounting = () => {
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedTab, setSelectedTab] = useState(0);
  const [error, setError] = useState('');

  // Estados de datos
  const [metrics, setMetrics] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [dashboardSummary, setDashboardSummary] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, [selectedPeriod]);

  const fetchAllData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');

    try {
      // Cargar datos en paralelo
      const [metricsData, monthlyChartData, topProductsData, summaryData] = await Promise.all([
        AccountingService.getFinancialMetrics(selectedPeriod),
        AccountingService.getMonthlyData(),
        AccountingService.getTopItems('products', 5),
        AccountingService.getDashboardSummary()
      ]);

      setMetrics(metricsData);
      setMonthlyData(monthlyChartData);
      setTopProducts(topProductsData);
      setDashboardSummary(summaryData);
    } catch (error) {
      console.error('Error fetching accounting data:', error);
      setError('Error al cargar los datos contables. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData(false);
  };

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatPercentage = (percentage) => {
    return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`;
  };

  // Configuración de gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true
        }
      }
    }
  };

  // Datos para gráfico de ingresos mensuales
  const monthlyRevenueData = {
    labels: monthlyData.map(item => item.monthName),
    datasets: [
      {
        label: 'Ingresos Totales',
        data: monthlyData.map(item => item.total),
        backgroundColor: Colors.primaryLight,
        borderColor: Colors.primary,
        borderWidth: 2,
      }
    ]
  };

  // Datos para gráfico de distribución de ingresos
  const revenueBreakdownData = {
    labels: ['Productos', 'Eventos', 'Cuotas AMPA'],
    datasets: [{
      data: metrics ? [
        metrics.breakdown.products || 0,
        metrics.breakdown.events || 0,
        metrics.breakdown.ampaFees || 0
      ] : [0, 0, 0],
      backgroundColor: [Colors.primary, Colors.accent, Colors.info],
      borderColor: [Colors.primaryDark, Colors.primaryDark, Colors.primaryDark],
      borderWidth: 2,
    }]
  };

  // Componente de métrica
  const MetricCard = ({ title, value, change, changeType, icon: Icon, color }) => (
    <Card sx={{ height: '100%', border: `2px solid ${Colors.primaryLight}` }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography color={Colors.textSecondary} gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ color: Colors.text, fontWeight: 'bold', mb: 1 }}>
              {value}
            </Typography>
            {change !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {changeType === 'growth' ? (
                  <TrendingUpIcon sx={{ color: Colors.success, fontSize: 16 }} />
                ) : (
                  <TrendingDownIcon sx={{ color: Colors.error, fontSize: 16 }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: changeType === 'growth' ? Colors.success : Colors.error,
                    fontWeight: 'bold'
                  }}
                >
                  {change}
                </Typography>
                <Typography variant="body2" color={Colors.textSecondary}>
                  vs período anterior
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: color || Colors.primary, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress size={60} sx={{ color: Colors.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: Colors.background, minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: Colors.text, fontWeight: 'bold' }}>
          Contabilidad
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              label="Período"
            >
              <MenuItem value="week">Semana</MenuItem>
              <MenuItem value="month">Mes</MenuItem>
              <MenuItem value="year">Año</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{
              borderColor: Colors.primary,
              color: Colors.primary,
              '&:hover': { borderColor: Colors.primaryDark }
            }}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              bgcolor: Colors.primary,
              '&:hover': { bgcolor: Colors.primaryDark }
            }}
          >
            Actualizar
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Métricas principales */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Ingresos Totales"
            value={formatCurrency(metrics?.currentPeriod.totalRevenue)}
            change={formatPercentage(metrics?.comparison.growthRate)}
            changeType={metrics?.comparison.isGrowth ? 'growth' : 'decline'}
            icon={EuroIcon}
            color={Colors.primary}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Transacciones"
            value={`${(metrics?.currentPeriod.orderCount || 0) + (metrics?.currentPeriod.eventPaymentCount || 0)}`}
            change={`${((metrics?.currentPeriod.orderCount || 0) + (metrics?.currentPeriod.eventPaymentCount || 0)) - ((metrics?.previousPeriod.orderCount || 0) + (metrics?.previousPeriod.eventPaymentCount || 0))}`}
            changeType="growth"
            icon={ReceiptIcon}
            color={Colors.accent}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Ticket Medio"
            value={formatCurrency(metrics?.currentPeriod.averageOrderValue)}
            icon={ShoppingCartIcon}
            color={Colors.primaryDark}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Eventos Pagados"
            value={metrics?.currentPeriod.eventPaymentCount || 0}
            icon={EventIcon}
            color={Colors.textSecondary}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab label="Resumen" />
          <Tab label="Análisis" />
          <Tab label="Top Productos" />
        </Tabs>
      </Paper>

      {/* Contenido por tabs */}
      {selectedTab === 0 && (
        <Grid container spacing={3}>
          {/* Gráfico de ingresos mensuales */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: Colors.text, fontWeight: 'bold' }}>
                  Evolución de Ingresos
                </Typography>
                <Box sx={{ height: 320 }}>
                  <Bar data={monthlyRevenueData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Distribución de ingresos */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: Colors.text, fontWeight: 'bold' }}>
                  Distribución de Ingresos
                </Typography>
                <Box sx={{ height: 320 }}>
                  <Pie data={revenueBreakdownData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: Colors.text, fontWeight: 'bold' }}>
                  Análisis Detallado
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>
                      Desglose por Fuente de Ingresos
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: Colors.primary }}>
                            <ShoppingCartIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary="Productos"
                          secondary={formatCurrency(metrics?.breakdown.products)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: Colors.accent }}>
                            <EventIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary="Eventos"
                          secondary={formatCurrency(metrics?.breakdown.events)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: Colors.info }}>
                            <AccountBalanceIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary="Cuotas AMPA"
                          secondary={formatCurrency(metrics?.breakdown.ampaFees)}
                        />
                      </ListItem>
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>
                      Comparativa con Período Anterior
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: Colors.background, borderRadius: 1 }}>
                      <Typography variant="body2" color={Colors.textSecondary}>
                        Período Actual: {formatCurrency(metrics?.currentPeriod.totalRevenue)}
                      </Typography>
                      <Typography variant="body2" color={Colors.textSecondary}>
                        Período Anterior: {formatCurrency(metrics?.previousPeriod.totalRevenue)}
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
                        Crecimiento: {formatPercentage(metrics?.comparison.growthRate || 0)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {selectedTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: Colors.text, fontWeight: 'bold' }}>
                  Top 5 Productos Más Vendidos
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {topProducts.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Producto</TableCell>
                          <TableCell align="right">Cantidad Vendida</TableCell>
                          <TableCell align="right">Ingresos</TableCell>
                          <TableCell align="right">Promedio por Unidad</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topProducts.map((product, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Chip
                                  label={`#${index + 1}`}
                                  size="small"
                                  sx={{ mr: 2, bgcolor: Colors.primaryLight }}
                                />
                                {product.name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{product.quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(product.revenue)}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(product.quantity > 0 ? product.revenue / product.quantity : 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <BarChartIcon sx={{ fontSize: 48, color: Colors.textSecondary, mb: 2 }} />
                    <Typography variant="h6" color={Colors.textSecondary}>
                      No hay datos de productos disponibles
                    </Typography>
                    <Typography variant="body2" color={Colors.textSecondary}>
                      Los productos aparecerán aquí cuando se realicen ventas.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Accounting;